import { getRouteApi } from '@tanstack/react-router';
import { CopyableText } from 'cpool-components/general/CopyableText';
import { DataTableRow } from 'cpool-components/general/DataTable';
import { CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_STATUSES, LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { ActionBar } from './Cells/ActionBar';
import { BankLogo } from './Cells/BankLogo';
import { OfferCommission } from './Cells/OfferCommission';
import { OfferTableCell } from './Cells/OfferTableCell';
import { SentAt } from './Cells/SentAt';
import { Status } from './Cells/Status';
import { getOfferFinancingPurpose } from './utils/getOfferFinancingPurpose';
import { RSV } from '~/components/RSV/RSV';
import { LEAD_DETAILS_OVERLAYS } from '~/features/leadDetails/constants/overlays';
import { useConsorsSubscriptionStatus } from '~/features/leadDetails/hooks/useConsorsSubscriptionStatus';
import useLead from '~/features/leadDetails/hooks/useLead';
import { useLeadDetailsOverlayState } from '~/features/leadDetails/hooks/useLeadDetailsOverlayState';
import { currencyFormat, dateTimeTwoDigitFormat, numberFormat } from '~/lib/i18n';

type Props = {
    acceptedOffer: SchemaLoanRequestResolver_GetAcceptedOffers[number];
    /** Sortable values, keyed by the column fields of the surrounding DataTable. */
    payload: Record<string, unknown>;
};

export const OfferManagementTableRow: React.FC<Props> = ({ acceptedOffer, payload }) => {
    const { t } = useTranslation();
    const router = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = router.useParams();
    const { data: lead } = useLead(leadId);
    const { data: consorsSubscription } = useConsorsSubscriptionStatus({
        loanRequestId: acceptedOffer.loanRequestId,
        subscriptionIdentifier:
            acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.CONSORS
                ? acceptedOffer.subscriptionIdentifier
                : undefined,
    });
    const { setOpen: setEditOfferOpen } = useLeadDetailsOverlayState({
        isRouteControlled: true,
        key: LEAD_DETAILS_OVERLAYS.EDIT_OFFER_ID,
        value: acceptedOffer.id,
    });

    if (!lead) return null;

    const isManualOffer = acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.MANUAL;

    const financingPurpose = getOfferFinancingPurpose(acceptedOffer, lead);

    const europaceOfferHasDocuments =
        acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.EUROPACE &&
        acceptedOffer.documents &&
        acceptedOffer.documents.length >= 1;

    const isConsorsOffer = acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.CONSORS;
    const consorsOfferDocumentCreated =
        isConsorsOffer &&
        consorsSubscription?.documentDownload?.status ===
            CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_STATUSES.SUCCESS;

    // either a manual offer with a document key, a Europace offer with documents or a Consors offer with a document created
    const enableOpenOffer =
        (isManualOffer && !!acceptedOffer.documents?.key) ||
        europaceOfferHasDocuments ||
        (isConsorsOffer && consorsOfferDocumentCreated);

    return (
        <DataTableRow
            data-testid={`offer-management-row-${acceptedOffer.id}`}
            payload={payload}
            className={isManualOffer ? 'cursor-pointer hover:bg-grey-table-hover duration-150' : undefined}
            onClick={isManualOffer ? () => setEditOfferOpen(true) : undefined}
        >
            <BankLogo
                bankLogo={'bankLogo' in acceptedOffer ? acceptedOffer.bankLogo : ''}
                bankName={'bank' in acceptedOffer ? acceptedOffer.bank : ''}
                description={'productDescription' in acceptedOffer ? acceptedOffer.productDescription : null}
                aggregateId={acceptedOffer.loanRequestId}
                offerId={acceptedOffer.id}
                enableOpenOffer={enableOpenOffer}
                manualOfferDocumentKey={isManualOffer ? acceptedOffer.documents?.key : undefined}
                acceptedOffer={acceptedOffer}
            />
            <OfferTableCell>{dateTimeTwoDigitFormat(new Date(acceptedOffer.acceptedAt ?? 0))}</OfferTableCell>
            <OfferTableCell>
                {financingPurpose ? t(`FUNDING_TYPES.${financingPurpose}`) : t('FUNDING_TYPES.not_specified')}
            </OfferTableCell>
            <OfferTableCell className="max-w-[140px]">
                <div className="flex flex-col">
                    {acceptedOffer.providerApplicationNumber ? (
                        <CopyableText
                            className="overflow-hidden text-ellipsis"
                            eventStopPropagation
                            text={acceptedOffer.providerApplicationNumber}
                        >
                            {acceptedOffer.providerApplicationNumber}
                        </CopyableText>
                    ) : (
                        <span>-</span>
                    )}
                    <span className="text-12 text-grey">{t(`OFFER_MANAGEMENT.PLATFORM.${acceptedOffer.source}`)}</span>
                </div>
            </OfferTableCell>
            <OfferTableCell>
                {acceptedOffer.creditAmount ? currencyFormat(acceptedOffer.creditAmount) : '-'}
            </OfferTableCell>
            <OfferTableCell>
                {acceptedOffer.runtime ? t('OFFER_MANAGEMENT.RUNTIME', { count: acceptedOffer.runtime }) : '-'}
            </OfferTableCell>
            <OfferTableCell>{acceptedOffer.rate ? currencyFormat(acceptedOffer.rate) : '-'}</OfferTableCell>
            <OfferTableCell>
                {acceptedOffer.interest
                    ? t('OFFER_MANAGEMENT.INTEREST', { interest: numberFormat(acceptedOffer.interest) })
                    : '-'}
            </OfferTableCell>
            <RSV
                className="px-4 py-3 text-left"
                applicant1ResidualDebtInsurance={
                    'applicant1ResidualDebtInsurance' in acceptedOffer && acceptedOffer.applicant1ResidualDebtInsurance
                        ? acceptedOffer.applicant1ResidualDebtInsurance
                        : null
                }
                applicant2ResidualDebtInsurance={
                    'applicant2ResidualDebtInsurance' in acceptedOffer && acceptedOffer.applicant2ResidualDebtInsurance
                        ? acceptedOffer.applicant2ResidualDebtInsurance
                        : null
                }
            />
            <OfferCommission
                leadId={leadId}
                source={acceptedOffer.source}
                id={acceptedOffer.id}
                responseCommissionInPercent={acceptedOffer.commissionResponseInPercent ?? null}
                receivedCommission={acceptedOffer.provision}
                kreditbetrag={acceptedOffer.creditAmount}
            />
            <Status
                leadId={leadId}
                status={acceptedOffer.status}
                loanRequestId={acceptedOffer.loanRequestId}
                source={acceptedOffer.source}
                id={acceptedOffer.id}
            />
            <SentAt id={acceptedOffer.id} leadId={leadId} sentAt={acceptedOffer.sentAt} source={acceptedOffer.source} />
            <ActionBar offer={acceptedOffer} />
        </DataTableRow>
    );
};
